/**
 * Shared flattening of KDM matrix data into one row per evaluated indicator/cell.
 *
 * This mirrors KnotView.vue's `allRows` computed so the Deliberation Report and the
 * Knot View present exactly the same data. It reads from the live `kdm` store.
 *
 * Cell key format in field_matrix_data is "SH-QPI-TOC"
 * (stakeholderId-qpiId-tocId). For KDM type 1 the cell holds a single
 * { judgmentId, sliderValue }; for types 2/3 the cell holds one entry per
 * indicator id: { [iprId]: { judgmentId, sliderValue } }.
 */
import { kdm, judgmentOptions, getIndicatorInfo } from '@/store/kdm';

export interface KnotRow {
  iprId: string;
  iprTitle: string;
  iprHref: string;
  evaluationId: string;
  evaluationLabel: string;
  evaluationColor: string;
  sliderValue: number;
  stakeholderId: string;
  stakeholderTitle: string;
  tocId: string;
  tocTitle: string;
  qpiId: string;
  qpiTitle: string;
  cellCoords: string;
  indicatorCoords: string;
  commentCount: number;
}

export function buildKnotRows(): KnotRow[] {
  const current = kdm.value;
  if (!current) return [];

  const rows: KnotRow[] = [];
  let matrixData: Record<string, any> = {};

  if (current.field_matrix_data) {
    try {
      matrixData = typeof current.field_matrix_data === 'string'
        ? JSON.parse(current.field_matrix_data)
        : current.field_matrix_data;
    } catch (e) {
      return rows;
    }
  }

  const stakeholders = current.stake_holders || [];
  const qpis = current.rows || [];
  const tocs = current.columns || [];

  const shMap = new Map(stakeholders.map(s => [s.id, s]));
  const qpiMap = new Map(qpis.map(q => [q.id, q]));
  const tocMap = new Map(tocs.map(t => [t.id, t]));

  Object.entries(matrixData).forEach(([cellKey, cellData]) => {
    if (cellKey === 'assignments' || !cellData || typeof cellData !== 'object') return;

    // Cell key: SH-QPI-TOC.
    const parts = cellKey.split('-');
    if (parts.length < 3) return;

    const [shId, qpiId, tocId] = parts;
    const sh = shMap.get(shId);
    const qpi = qpiMap.get(qpiId);
    const toc = tocMap.get(tocId);
    if (!sh || !qpi || !toc) return;

    // KDM Type 1: a single direct judgment per cell.
    if (current.kdm_type === 1) {
      if ((cellData as any).judgmentId) {
        const judgment = judgmentOptions.value.get((cellData as any).judgmentId);
        rows.push({
          iprId: 'direct',
          iprTitle: 'Direct Evaluation',
          iprHref: '',
          evaluationId: (cellData as any).judgmentId,
          evaluationLabel: judgment?.title || judgment?.color || '',
          evaluationColor: judgment?.color || '#ccc',
          sliderValue: (cellData as any).sliderValue || 0,
          stakeholderId: shId,
          stakeholderTitle: sh.title,
          tocId,
          tocTitle: toc.title,
          qpiId,
          qpiTitle: qpi.title,
          cellCoords: cellKey,
          indicatorCoords: cellKey,
          commentCount: current.comment_summary?.[cellKey]?.count || 0,
        });
      }
      return;
    }

    // KDM Type 2/3: one entry per mobilized indicator.
    Object.entries(cellData as Record<string, any>).forEach(([indicatorId, indicatorData]) => {
      if (!indicatorData || typeof indicatorData !== 'object' || !indicatorData.judgmentId) return;

      const info = getIndicatorInfo(indicatorId);
      const judgment = judgmentOptions.value.get(indicatorData.judgmentId);
      const indicatorCoords = `${cellKey}-${indicatorId}`;

      rows.push({
        iprId: indicatorId,
        iprTitle: info.title || indicatorId,
        iprHref: info.href || `/node/${indicatorId}`,
        evaluationId: indicatorData.judgmentId,
        evaluationLabel: judgment?.title || judgment?.color || '',
        evaluationColor: judgment?.color || '#ccc',
        sliderValue: indicatorData.sliderValue || 0,
        stakeholderId: shId,
        stakeholderTitle: sh.title,
        tocId,
        tocTitle: toc.title,
        qpiId,
        qpiTitle: qpi.title,
        cellCoords: cellKey,
        indicatorCoords,
        commentCount: current.comment_summary?.[indicatorCoords]?.count || 0,
      });
    });
  });

  return rows;
}
